/**
 * Permissions Store (Zustand)
 * Hanterar användarens permissions och features
 * Kombinerar RBAC-permissions med paket-baserade features
 */

import { create } from 'zustand';
import { rbacService } from '@/services/rbac/rbacService';
import { packageAccessService, type MyFeaturesResponse } from '@/services/packages/packageAccessService';
import type { UserPermissions, Feature } from '@/types/rbac';

const PERMISSIONS_TTL_MS = 5 * 60 * 1000; // 5 minuter

/** Samma features som backend skipPackageCheck — RBAC räcker, paket krävs inte. */
const RBAC_ONLY_FEATURES = new Set(['ADMIN_FLINKEY', 'FLINKY_BOXES', 'ADMIN_SUPPORT']);

interface PermissionsState {
  permissions: UserPermissions | null;
  packageFeatures: MyFeaturesResponse | null;
  isLoading: boolean;
  error: string | null;
  lastFetchedAt: number | null;
  fetchPermissions: (userId?: string, organizationId?: string) => Promise<void>;
  setAdminPermissions: (userId: string, organizationId: string) => void;
  hasFeatureAccess: (featureCode: string) => boolean;
  hasPackageAccess: (featureCode: string) => boolean;
  hasActionAccess: (featureCode: string, actionCode: string) => boolean;
  getAccessibleFeatures: () => Feature[];
  isDemo: () => boolean;
  isTrial: () => boolean;
  getTrialDaysRemaining: () => number | null;
  clearPermissions: () => void;
  isCacheValid: () => boolean;
}

export const usePermissionsStore = create<PermissionsState>((set, get) => ({
  permissions: null,
  packageFeatures: null,
  isLoading: false,
  error: null,
  lastFetchedAt: null,

  isCacheValid: () => {
    const { lastFetchedAt, permissions } = get();
    if (!permissions || !lastFetchedAt) return false;
    return Date.now() - lastFetchedAt < PERMISSIONS_TTL_MS;
  },

  setAdminPermissions: (userId: string, organizationId: string) => {
    set({
      permissions: {
        userId,
        organizationId,
        roles: [],
        permissions: [],
        features: [],
      },
      packageFeatures: {
        features: [],
        isUnlimited: true,
        subscriptionType: 'admin',
      },
      isLoading: false,
      error: null,
      lastFetchedAt: Date.now(),
    });
  },

  fetchPermissions: async (userId?: string, organizationId?: string) => {
    // TTL-check: skippa om vi redan har färska permissions
    if (get().isCacheValid()) return;

    set({ isLoading: true, error: null });
    try {
      const [permissions, packageFeatures] = await Promise.all([
        rbacService.getUserPermissions(userId, organizationId),
        packageAccessService.getMyFeatures().catch(() => null),
      ]);
      set({ permissions, packageFeatures, isLoading: false, error: null, lastFetchedAt: Date.now() });
    } catch (error: any) {
      set({
        permissions: null,
        packageFeatures: null,
        isLoading: false,
        error: error.message || 'Failed to fetch permissions',
        lastFetchedAt: null,
      });
    }
  },

  hasFeatureAccess: (featureCode: string): boolean => {
    const { permissions, packageFeatures } = get();

    // Global admin / SYS_ADMIN (isUnlimited) har tillgång till alla features
    if (packageFeatures?.isUnlimited) {
      return true;
    }
    
    // Kontrollera först paket-åtkomst (hoppa över för RBAC-only features)
    if (
      !RBAC_ONLY_FEATURES.has(featureCode)
      && packageFeatures?.features
      && !packageFeatures.features.includes(featureCode)
    ) {
      return false; // Inte tillgänglig via paket
    }
    
    // Kontrollera sedan RBAC
    if (!permissions) return false;
    return permissions.features.some(f => f.code === featureCode && f.is_active);
  },

  hasPackageAccess: (featureCode: string): boolean => {
    const { packageFeatures } = get();
    
    // Om paket-features är unlimited (admin), alla är tillgängliga
    if (packageFeatures?.isUnlimited) return true;
    
    // Kontrollera om feature finns i paket
    if (!packageFeatures?.features) return true; // Ingen begränsning
    return packageFeatures.features.includes(featureCode);
  },

  hasActionAccess: (featureCode: string, actionCode: string): boolean => {
    const { permissions, packageFeatures } = get();
    if (!permissions) return false;
    
    // Global admin (isUnlimited) har alltid åtkomst till alla actions
    if (packageFeatures?.isUnlimited) return true;

    // Kontrollera paket-åtkomst först (hoppa över för RBAC-only features)
    if (
      !RBAC_ONLY_FEATURES.has(featureCode)
      && packageFeatures?.features
      && !packageFeatures.isUnlimited
    ) {
      if (!packageFeatures.features.includes(featureCode)) {
        return false;
      }
    }

    return permissions.permissions.some(p => {
      if (p.feature.code !== featureCode) return false;
      
      // Om action är null, betyder det alla actions för feature
      if (p.action === null) return true;
      
      return p.action.code === actionCode;
    });
  },

  getAccessibleFeatures: (): Feature[] => {
    const { permissions, packageFeatures } = get();
    if (!permissions) return [];

    // Om användaren har features, returnera dem
    if (permissions.features && permissions.features.length > 0) {
      let features = permissions.features.filter(f => f.is_active);
      
      // Filtrera baserat på paket om det finns begränsningar
      if (packageFeatures?.features && !packageFeatures.isUnlimited) {
        features = features.filter(
          (f) => RBAC_ONLY_FEATURES.has(f.code) || packageFeatures.features.includes(f.code)
        );
      }
      
      return features.sort((a, b) => a.display_order - b.display_order);
    }

    // Fallback: returnera tom array
    return [];
  },

  isDemo: (): boolean => {
    const { packageFeatures } = get();
    return packageFeatures?.subscriptionType === 'demo';
  },

  isTrial: (): boolean => {
    const { packageFeatures } = get();
    return packageFeatures?.subscriptionType === 'trial';
  },

  getTrialDaysRemaining: (): number | null => {
    const { packageFeatures } = get();
    if (!packageFeatures?.modules) return null;
    
    // Hitta trial-modul med minst dagar kvar
    const trialModules = packageFeatures.modules.filter(
      m => m.subscriptionType === 'trial' && m.expiresAt
    );
    
    if (trialModules.length === 0) return null;
    
    const now = new Date();
    let minDays = Infinity;
    
    trialModules.forEach(m => {
      const expires = new Date(m.expiresAt!);
      const days = Math.ceil((expires.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
      if (days < minDays) minDays = days;
    });
    
    return minDays === Infinity ? null : minDays;
  },

  clearPermissions: () => {
    set({ permissions: null, packageFeatures: null, isLoading: false, error: null, lastFetchedAt: null });
  },
}));

